import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { adminService } from '../../lib/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const OverviewTabEnhanced = ({ stats }) => {
  const [tendances, setTendances] = useState(null);
  const [topEntreprises, setTopEntreprises] = useState([]);
  const [statsEmployes, setStatsEmployes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [critereTop, setCritereTop] = useState('volume');
  const [activeChart, setActiveChart] = useState('tendances');

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (critereTop) {
      fetchTopEntreprises(critereTop);
    }
  }, [critereTop]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [tendancesData, topData, statsEmpData] = await Promise.all([
        adminService.getAnalyticsTrends(),
        adminService.getTopCompanies(critereTop, 10),
        adminService.getEmployeeStatistics()
      ]);
      
      setTendances(tendancesData);
      setTopEntreprises(topData.classement || []);
      setStatsEmployes(statsEmpData);
    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopEntreprises = async (critere) => {
    try {
      const data = await adminService.getTopCompanies(critere, 10);
      setTopEntreprises(data.classement || []);
    } catch (error) {
      console.error('Erreur lors du chargement du top entreprises:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs Principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Entreprises</p>
                <p className="text-3xl font-bold">{stats?.total_entreprises || 0}</p>
                <p className="text-blue-100 text-xs mt-1">
                  {stats?.entreprises_actives || 0} actives
                </p>
              </div>
              <div className="text-4xl opacity-80">🏢</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Employés</p>
                <p className="text-3xl font-bold">{stats?.total_employes || 0}</p>
                <p className="text-green-100 text-xs mt-1">
                  Total enregistrés
                </p>
              </div>
              <div className="text-4xl opacity-80">👥</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Avances</p>
                <p className="text-3xl font-bold">{stats?.total_avances || 0}</p>
                <p className="text-purple-100 text-xs mt-1">
                  Total traitées
                </p>
              </div>
              <div className="text-4xl opacity-80">💳</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Volume</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.volume_total || 0)}</p>
                <p className="text-orange-100 text-xs mt-1">
                  Total échangé
                </p>
              </div>
              <div className="text-4xl opacity-80">📊</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation des graphiques */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Button
              variant={activeChart === 'tendances' ? 'default' : 'outline'}
              onClick={() => setActiveChart('tendances')}
              size="sm"
            >
              📈 Tendances
            </Button>
            <Button
              variant={activeChart === 'top' ? 'default' : 'outline'}
              onClick={() => setActiveChart('top')}
              size="sm"
            >
              🏆 Top Entreprises
            </Button>
            <Button
              variant={activeChart === 'employes' ? 'default' : 'outline'}
              onClick={() => setActiveChart('employes')}
              size="sm"
            >
              👥 Statistiques Employés
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Graphique Tendances */}
      {activeChart === 'tendances' && tendances && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>📈 Tendances des 6 Derniers Mois</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {/* Graphique des Avances */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Nombre d'Avances par Mois</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={tendances.tendances_mensuelles}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mois" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="nb_avances" fill="#3B82F6" name="Nb Avances" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Graphique du Volume */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Volume Financier Mensuel (GNF)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={tendances.tendances_mensuelles}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mois" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="montant_total" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        name="Volume Total"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="frais_collectes" 
                        stroke="#F59E0B" 
                        strokeWidth={2}
                        name="Frais Collectés"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Graphique Croissance */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Croissance (Entreprises & Employés)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={tendances.tendances_mensuelles}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mois" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="nouvelles_entreprises" 
                        stroke="#8B5CF6" 
                        strokeWidth={2}
                        name="Nouvelles Entreprises"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="nouveaux_employes" 
                        stroke="#EC4899" 
                        strokeWidth={2}
                        name="Nouveaux Employés"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Entreprises */}
      {activeChart === 'top' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>🏆 Classement des Entreprises</CardTitle>
              <select
                value={critereTop}
                onChange={(e) => setCritereTop(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="volume">Par Volume</option>
                <option value="nb_avances">Par Nombre d'Avances</option>
                <option value="nb_employes">Par Nombre d'Employés</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {topEntreprises.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🏢</div>
                <p className="text-gray-600">Aucune donnée disponible</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Graphique en barres */}
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={topEntreprises.slice(0, 5)} 
                    layout="vertical"
                    margin={{ left: 150 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="raison_sociale" 
                      width={140}
                    />
                    <Tooltip 
                      formatter={(value) => 
                        critereTop === 'volume' 
                          ? formatCurrency(value) 
                          : value
                      }
                    />
                    <Bar 
                      dataKey={
                        critereTop === 'volume' ? 'volume_total' :
                        critereTop === 'nb_avances' ? 'nb_avances' :
                        'nb_employes'
                      }
                      fill="#3B82F6"
                      name={
                        critereTop === 'volume' ? 'Volume Total' :
                        critereTop === 'nb_avances' ? 'Nb Avances' :
                        'Nb Employés'
                      }
                    />
                  </BarChart>
                </ResponsiveContainer>

                {/* Tableau détaillé */}
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Rang</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Entreprise</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Employés</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Avances</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Volume Total</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Frais Générés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topEntreprises.map((entreprise, index) => (
                        <tr key={entreprise.entreprise_id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className={`font-bold text-lg ${
                              index === 0 ? 'text-yellow-500' :
                              index === 1 ? 'text-gray-400' :
                              index === 2 ? 'text-orange-600' :
                              'text-gray-600'
                            }`}>
                              {index + 1}
                              {index === 0 && ' 🥇'}
                              {index === 1 && ' 🥈'}
                              {index === 2 && ' 🥉'}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium">{entreprise.raison_sociale}</td>
                          <td className="py-3 px-4">{entreprise.nb_employes}</td>
                          <td className="py-3 px-4">{entreprise.nb_avances}</td>
                          <td className="py-3 px-4 text-green-600 font-medium">
                            {formatCurrency(entreprise.volume_total)}
                          </td>
                          <td className="py-3 px-4 text-blue-600 font-medium">
                            {formatCurrency(entreprise.frais_generes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Statistiques Employés */}
      {activeChart === 'employes' && statsEmployes && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl mb-2">👥</div>
                  <p className="text-sm text-gray-600 mb-1">Total Employés</p>
                  <p className="text-3xl font-bold text-blue-600">{statsEmployes.total_employes}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-sm text-gray-600 mb-1">Actifs</p>
                  <p className="text-3xl font-bold text-green-600">{statsEmployes.employes_actifs}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl mb-2">💳</div>
                  <p className="text-sm text-gray-600 mb-1">Avec Avances</p>
                  <p className="text-3xl font-bold text-purple-600">{statsEmployes.employes_avec_avances}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="text-sm text-gray-600 mb-1">Taux Utilisation</p>
                  <p className="text-3xl font-bold text-orange-600">{statsEmployes.taux_utilisation}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Distribution des Employés par Entreprise</CardTitle>
            </CardHeader>
            <CardContent>
              {statsEmployes.distribution_par_entreprise?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Graphique Pie */}
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={statsEmployes.distribution_par_entreprise}
                        dataKey="nb_employes"
                        nameKey="entreprise"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        label={(entry) => `${entry.entreprise}: ${entry.nb_employes}`}
                      >
                        {statsEmployes.distribution_par_entreprise.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Liste détaillée */}
                  <div className="space-y-3">
                    {statsEmployes.distribution_par_entreprise.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full mr-3"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="font-medium text-gray-900">{item.entreprise}</span>
                        </div>
                        <span className="text-lg font-bold text-blue-600">{item.nb_employes}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📊</div>
                  <p className="text-gray-600">Aucune donnée de distribution disponible</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                  <h3 className="font-medium text-green-900 mb-2">💰 Montant Moyen par Avance</h3>
                  <p className="text-3xl font-bold text-green-700">
                    {formatCurrency(statsEmployes.montant_moyen_avance)}
                  </p>
                </div>

                <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">📈 Taux d'Utilisation Global</h3>
                  <p className="text-3xl font-bold text-blue-700">
                    {statsEmployes.taux_utilisation}%
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    {statsEmployes.employes_avec_avances} sur {statsEmployes.total_employes} employés
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OverviewTabEnhanced;
